import React from "react";

import { graphql } from "relay-runtime";

import { useSyncedPersistentState } from "~/actionItems/hooks/useSyncedPersistentState";
import type { CreateOrImportProps } from "~/integrations/actionItemIntegration/actionItemLinkingModal";
import { FolderSelector } from "~/integrations/clickup/selectors/ClickupFolderSelector";
import { ListSelector } from "~/integrations/clickup/selectors/ClickupListSelector";
import { SpaceSelector } from "~/integrations/clickup/selectors/ClickupSpaceSelector";
import { WorkspaceSelector } from "~/integrations/clickup/selectors/ClickupWorkspaceSelector";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Input } from "~/ui/legacyFellowIndex";
import toasty from "~/ui/toast";

import { clickupStore } from "./store";

import type { CreateNewExternalTicketPaneMutation } from "~/__generated__/CreateNewExternalTicketPaneMutation.graphql";

import styles from "./css/styles.module.css";

const createExternalTicket = graphql`
	mutation CreateNewExternalTicketPaneMutation(
		$blockKey: String!
		$noteId: ID!
		$title: String!
		$destinationInfo: JSON!
	) {
		sendActionItemToIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				title: $title
				destinationInfo: $destinationInfo
				integrationProvider: CLICKUP
			}
		) {
			clientMutationId
		}
	}
`;

// TODO: new name
function link(blockKey: string, noteId: string, title: string, listId: string, workspaceId: string) {
	return commitMutationPromise<CreateNewExternalTicketPaneMutation>(environment, {
		mutation: createExternalTicket,
		// eslint-disable-next-line camelcase
		variables: { blockKey, noteId, title, destinationInfo: { list_id: listId, workspace_id: workspaceId } },
	});
}

export function CreateNewExternalTicketPane({
	setCanSubmit,
	registerSubmit,
	initialText,
	blockKey,
	noteId,
}: CreateOrImportProps) {
	const [workspaceId, setWorkspaceId] = useSyncedPersistentState<string | undefined>(
		clickupStore,
		"workspaceId",
		undefined,
	);
	const [spaceId, setSpaceId] = useSyncedPersistentState<string | undefined>(clickupStore, "spaceId", undefined);
	const [folderId, setFolderId] = useSyncedPersistentState<string | undefined>(clickupStore, "folderId", undefined);
	const [listId, setListId] = useSyncedPersistentState<string | undefined>(clickupStore, "listId", undefined);
	const [title, setTitle] = React.useState<string | undefined>(initialText ? initialText : undefined);

	const onTitleChange = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
		// @ts-ignore
		setTitle(e.target.value);
	}, []);

	const submit = React.useCallback(async () => {
		if (!title || !listId || !workspaceId) {
			throw new Error("Submission validation step failed");
		}

		try {
			await link(blockKey, noteId, title, listId, workspaceId);
		} catch (e) {
			if (Array.isArray(e)) {
				for (let exception of e) {
					if ("message" in exception) toasty.error(exception.message);
				}
			}
		}
		return true;
	}, [blockKey, noteId, title, listId, workspaceId]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		setCanSubmit(Boolean(workspaceId && spaceId && listId && title));
	}, [setCanSubmit, workspaceId, spaceId, listId, title]);

	return (
		<div className={styles.container}>
			<label className={styles.workspace}>
				<div className={styles.label}>Workspace</div>
				<WorkspaceSelector workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
			</label>
			<label className={styles.space}>
				<div className={styles.label}>Space</div>
				<SpaceSelector workspaceId={workspaceId} spaceId={spaceId} setSpaceId={setSpaceId} />
			</label>
			<label className={styles.folder}>
				<div className={styles.label}>Folder</div>
				<FolderSelector spaceId={spaceId} folderId={folderId} setFolderId={setFolderId} />
			</label>
			<label className={styles.list}>
				<div className={styles.label}>List</div>
				<ListSelector spaceId={spaceId} folderId={folderId} listId={listId} setListId={setListId} />
			</label>
			<label className={styles.title}>
				<div className={styles.label}>Title</div>
				<Input
					name="clickup_task_title"
					className={styles.titleInput}
					type="text"
					placeholder="Set a title for this task"
					value={title}
					onChange={onTitleChange}
				/>
			</label>
			<div className={styles.help}>
				The task name, due date, assignee, and status will automatically sync with Clickup.
			</div>
		</div>
	);
}
